import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, Plus, ClipboardList, Trash2, Edit3,
  Search, Clock, ChevronDown, ChevronUp, X,
  GripVertical, CheckCircle, AlertCircle, FileText,
  Copy, Download, Eye, Package, Wrench, Printer
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  active: { label: 'Active', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  complete: { label: 'Complete', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
};

const MATERIAL_LABELS = {
  'aluminum-6061': 'Aluminum 6061-T6',
  'steel-4140': 'Steel 4140',
  'steel-1018': 'Steel 1018',
  'stainless-304': 'Stainless 304',
  'stainless-316': 'Stainless 316',
  'titanium-6al4v': 'Titanium 6Al-4V',
  'brass-360': 'Brass C360',
  'cast-iron': 'Cast Iron',
  'plastic-delrin': 'Delrin/Acetal',
};

const formatTime = (mins) => {
  if (!mins) return '0m';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const JobPlansPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [viewingGcode, setViewingGcode] = useState(null);
  const [showProgramPicker, setShowProgramPicker] = useState(false);
  const [printingPlan, setPrintingPlan] = useState(null);

  // Editor form state
  const [formName, setFormName] = useState('');
  const [formPartNumber, setFormPartNumber] = useState('');
  const [formMaterial, setFormMaterial] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formStatus, setFormStatus] = useState('draft');
  const [formOps, setFormOps] = useState([]);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/job-plans`);
      setPlans(res.data);
    } catch {
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPrograms = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/programs`);
      setPrograms(res.data);
    } catch {
      setPrograms([]);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
    fetchPrograms();
  }, [fetchPlans, fetchPrograms]);

  // Check for pending "add to plan" from SavedProgramsPage
  useEffect(() => {
    const pending = sessionStorage.getItem('add_to_job_plan');
    if (pending) {
      sessionStorage.removeItem('add_to_job_plan');
      const prog = JSON.parse(pending);
      openNewPlan();
      setFormOps([{
        program_id: prog.id,
        program_name: prog.name,
        op_number: 10,
        description: prog.description || '',
        est_setup_min: 0,
        est_cycle_min: 0,
        tools_summary: prog.tools_used?.map(t => `T${t.tool_number} ${t.description}`).join(', ') || '',
      }]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNewPlan = () => {
    setEditingPlan(null);
    setFormName('');
    setFormPartNumber('');
    setFormMaterial('');
    setFormNotes('');
    setFormStatus('draft');
    setFormOps([]);
    setShowEditor(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setFormName(plan.name);
    setFormPartNumber(plan.part_number || '');
    setFormMaterial(plan.material || '');
    setFormNotes(plan.notes || '');
    setFormStatus(plan.status || 'draft');
    setFormOps(plan.operations?.map(o => ({ ...o })) || []);
    setShowEditor(true);
  };

  const savePlan = async () => {
    if (!formName.trim()) return;
    const payload = {
      name: formName.trim(),
      part_number: formPartNumber.trim() || null,
      material: formMaterial || null,
      notes: formNotes.trim() || null,
      status: formStatus,
      operations: formOps.map((op, i) => ({
        ...op,
        op_number: op.op_number || (i + 1) * 10,
      })),
    };
    try {
      if (editingPlan) {
        await axios.put(`${API}/job-plans/${editingPlan.id}`, payload);
      } else {
        await axios.post(`${API}/job-plans`, payload);
      }
      setShowEditor(false);
      fetchPlans();
    } catch (err) {
      console.error('Save failed:', err);
    }
  };

  const deletePlan = async (id) => {
    try {
      await axios.delete(`${API}/job-plans/${id}`);
      setPlans(plans.filter(p => p.id !== id));
    } catch (err) {
      console.error('Delete failed:', err);
    }
    setShowDeleteConfirm(null);
  };

  const addOperation = (prog) => {
    const nextOp = formOps.length > 0
      ? Math.max(...formOps.map(o => o.op_number || 0)) + 10
      : 10;
    setFormOps([...formOps, {
      program_id: prog?.id || null,
      program_name: prog?.name || 'Manual Operation',
      op_number: nextOp,
      description: prog?.description || '',
      est_setup_min: 0,
      est_cycle_min: 0,
      tools_summary: prog?.tools_used?.map(t => `T${t.tool_number} ${t.description}`).join(', ') || '',
    }]);
    setShowProgramPicker(false);
  };

  const removeOperation = (index) => {
    setFormOps(formOps.filter((_, i) => i !== index));
  };

  const moveOp = (index, direction) => {
    const newOps = [...formOps];
    const target = index + direction;
    if (target < 0 || target >= newOps.length) return;
    [newOps[index], newOps[target]] = [newOps[target], newOps[index]];
    setFormOps(newOps);
  };

  const updateOp = (index, field, value) => {
    const newOps = [...formOps];
    newOps[index] = { ...newOps[index], [field]: value };
    setFormOps(newOps);
  };

  const totalSetup = formOps.reduce((s, o) => s + (parseFloat(o.est_setup_min) || 0), 0);
  const totalCycle = formOps.reduce((s, o) => s + (parseFloat(o.est_cycle_min) || 0), 0);

  const filteredPlans = plans.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.part_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const fetchGcode = async (programId) => {
    try {
      const res = await axios.get(`${API}/programs/${programId}`);
      setViewingGcode(res.data);
    } catch {
      setViewingGcode(null);
    }
  };

  const printTraveler = (plan) => {
    setPrintingPlan(plan);
    setTimeout(() => window.print(), 300);
  };

  // ─── RENDER ─────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-industrial-bg pb-20" data-testid="job-plans-page">
      {/* Sub Header */}
      <div className="sticky top-14 z-20 bg-industrial-surface border-b border-industrial-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          <h2 className="font-heading font-bold text-industrial-safety flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            JOB PLANS
          </h2>
          <button
            onClick={openNewPlan}
            className="p-2 rounded-lg hover:bg-industrial-surface-highlight transition-colors"
            data-testid="new-plan-btn"
          >
            <Plus className="w-5 h-5 text-industrial-text-secondary" />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-industrial-text-secondary" />
          <input
            type="text"
            placeholder="Search plans, part numbers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-industrial-surface border border-industrial-border rounded-lg pl-10 pr-4 py-3 text-industrial-text placeholder:text-industrial-text-secondary"
            data-testid="search-plans-input"
          />
        </div>
      </div>

      {/* Plans List */}
      <div className="px-4 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-industrial-safety/30 border-t-industrial-safety rounded-full animate-spin" />
          </div>
        ) : filteredPlans.length === 0 ? (
          <div className="bg-industrial-surface border border-industrial-border rounded-lg p-8 text-center">
            <ClipboardList className="w-12 h-12 mx-auto text-industrial-text-secondary mb-3" />
            <p className="text-industrial-text-secondary mb-1">
              {searchQuery ? 'No plans match your search' : 'No job plans yet'}
            </p>
            <p className="text-xs text-industrial-text-secondary mb-4">
              Create a plan to sequence your saved programs into a complete job.
            </p>
            <button
              onClick={openNewPlan}
              className="px-6 py-2 bg-industrial-safety text-black rounded-lg font-medium hover:bg-industrial-safety/90 transition-colors"
              data-testid="create-first-plan-btn"
            >
              Create Job Plan
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPlans.map((plan) => {
              const statusCfg = STATUS_CONFIG[plan.status] || STATUS_CONFIG.draft;
              const totalTime = (plan.total_setup_min || 0) + (plan.total_cycle_min || 0);
              const isExpanded = expandedPlan === plan.id;

              return (
                <div
                  key={plan.id}
                  className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden hover:border-industrial-safety/50 transition-all"
                  data-testid={`plan-card-${plan.id}`}
                >
                  {/* Card Header */}
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-industrial-text truncate">{plan.name}</h3>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${statusCfg.color}`}>
                            {statusCfg.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-industrial-text-secondary flex-wrap">
                          {plan.part_number && (
                            <span className="flex items-center gap-1">
                              <Package className="w-3 h-3" />
                              {plan.part_number}
                            </span>
                          )}
                          {plan.material && (
                            <span>{MATERIAL_LABELS[plan.material] || plan.material}</span>
                          )}
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {plan.operations?.length || 0} ops
                          </span>
                          <span className="flex items-center gap-1 text-industrial-safety">
                            <Clock className="w-3 h-3" />
                            {formatTime(totalTime)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          onClick={() => printTraveler(plan)}
                          className="p-2 rounded hover:bg-industrial-surface-highlight transition-colors"
                          title="Print Traveler"
                          data-testid={`print-plan-${plan.id}`}
                        >
                          <Printer className="w-4 h-4 text-industrial-text-secondary" />
                        </button>
                        <button
                          onClick={() => openEditPlan(plan)}
                          className="p-2 rounded hover:bg-industrial-surface-highlight transition-colors"
                          title="Edit"
                          data-testid={`edit-plan-${plan.id}`}
                        >
                          <Edit3 className="w-4 h-4 text-industrial-text-secondary" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(plan.id)}
                          className="p-2 rounded hover:bg-red-500/20 transition-colors"
                          title="Delete"
                          data-testid={`delete-plan-${plan.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>

                    {/* Expand toggle */}
                    <button
                      onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                      className="flex items-center gap-1 mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                      data-testid={`expand-plan-${plan.id}`}
                    >
                      <span>Operation Sequence</span>
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  </div>

                  {/* Expanded Operations */}
                  {isExpanded && (
                    <div className="border-t border-industrial-border bg-industrial-bg/50 p-4 space-y-2">
                      {plan.operations?.length === 0 ? (
                        <p className="text-xs text-industrial-text-secondary text-center py-2">No operations defined</p>
                      ) : (
                        <>
                          {/* Time summary bar */}
                          <div className="flex items-center gap-4 text-xs mb-3 p-2 bg-industrial-surface rounded-lg">
                            <span className="text-industrial-text-secondary">Setup: <span className="text-industrial-text font-medium">{formatTime(plan.total_setup_min)}</span></span>
                            <span className="text-industrial-text-secondary">Cycle: <span className="text-industrial-text font-medium">{formatTime(plan.total_cycle_min)}</span></span>
                            <span className="ml-auto text-industrial-safety font-medium">Total: {formatTime(totalTime)}</span>
                          </div>
                          {plan.operations.map((op, i) => (
                            <div key={i} className="flex items-center gap-3 bg-industrial-surface rounded-lg p-3">
                              <div className="w-8 h-8 rounded-full bg-industrial-safety/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-mono font-bold text-industrial-safety">{op.op_number}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-industrial-text truncate">{op.program_name}</div>
                                {op.description && <div className="text-xs text-industrial-text-secondary truncate">{op.description}</div>}
                                {op.tools_summary && (
                                  <div className="text-[10px] text-industrial-text-secondary mt-0.5 flex items-center gap-1 truncate">
                                    <Wrench className="w-2.5 h-2.5" />{op.tools_summary}
                                  </div>
                                )}
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="text-xs text-industrial-text-secondary">
                                  S:{formatTime(op.est_setup_min)} C:{formatTime(op.est_cycle_min)}
                                </div>
                              </div>
                              {op.program_id && (
                                <button
                                  onClick={() => fetchGcode(op.program_id)}
                                  className="p-1.5 rounded hover:bg-industrial-surface-highlight transition-colors flex-shrink-0"
                                  title="View G-Code"
                                  data-testid={`view-op-gcode-${i}`}
                                >
                                  <Eye className="w-3.5 h-3.5 text-blue-400" />
                                </button>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                      {plan.notes && (
                        <div className="mt-2 p-2 bg-industrial-surface rounded-lg text-xs text-industrial-text-secondary">
                          <span className="font-medium text-industrial-text">Notes: </span>{plan.notes}
                        </div>
                      )}
                      <div className="text-[10px] text-industrial-text-secondary pt-1">
                        Updated {formatDate(plan.updated_at)}
                      </div>
                    </div>
                  )}

                  {/* Delete Confirmation */}
                  {showDeleteConfirm === plan.id && (
                    <div className="border-t border-industrial-border p-3 flex items-center justify-between bg-red-500/5">
                      <span className="text-sm text-red-400">Delete this job plan?</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowDeleteConfirm(null)}
                          className="px-3 py-1 text-sm bg-industrial-surface-highlight rounded text-industrial-text"
                          data-testid={`cancel-delete-plan-${plan.id}`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => deletePlan(plan.id)}
                          className="px-3 py-1 text-sm bg-red-500 rounded text-white"
                          data-testid={`confirm-delete-plan-${plan.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Editor Modal ──────────────────────────────────── */}
      {showEditor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 overflow-y-auto">
          <div className="bg-industrial-surface border border-industrial-border rounded-xl w-full max-w-2xl my-8" data-testid="plan-editor-modal">
            {/* Editor Header */}
            <div className="flex items-center justify-between p-4 border-b border-industrial-border">
              <h3 className="font-heading font-bold text-industrial-text">
                {editingPlan ? 'Edit Job Plan' : 'New Job Plan'}
              </h3>
              <button
                onClick={() => setShowEditor(false)}
                className="p-2 hover:bg-industrial-surface-highlight rounded-lg transition-colors"
                data-testid="close-editor-btn"
              >
                <X className="w-5 h-5 text-industrial-text-secondary" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Plan Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs text-industrial-text-secondary mb-1">Plan Name *</label>
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g. Shaft Assembly Job"
                    className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-3 py-2 text-industrial-text placeholder:text-industrial-text-secondary"
                    data-testid="plan-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-industrial-text-secondary mb-1">Part Number</label>
                  <input
                    type="text"
                    value={formPartNumber}
                    onChange={(e) => setFormPartNumber(e.target.value)}
                    placeholder="e.g. SH-1024"
                    className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-3 py-2 text-industrial-text placeholder:text-industrial-text-secondary"
                    data-testid="plan-part-number-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-industrial-text-secondary mb-1">Material</label>
                  <select
                    value={formMaterial}
                    onChange={(e) => setFormMaterial(e.target.value)}
                    className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-3 py-2 text-industrial-text"
                    data-testid="plan-material-select"
                  >
                    <option value="">Select material</option>
                    {Object.entries(MATERIAL_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-industrial-text-secondary mb-1">Status</label>
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value)}
                    className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-3 py-2 text-industrial-text"
                    data-testid="plan-status-select"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="complete">Complete</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-industrial-text-secondary mb-1">Notes</label>
                  <input
                    type="text"
                    value={formNotes}
                    onChange={(e) => setFormNotes(e.target.value)}
                    placeholder="Special instructions..."
                    className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-3 py-2 text-industrial-text placeholder:text-industrial-text-secondary"
                    data-testid="plan-notes-input"
                  />
                </div>
              </div>

              {/* Operations */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-industrial-text">Operations</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowProgramPicker(true)}
                      className="px-3 py-1.5 text-xs bg-industrial-safety/20 border border-industrial-safety/30 rounded-lg text-industrial-safety hover:bg-industrial-safety/30 transition-colors flex items-center gap-1"
                      data-testid="add-from-programs-btn"
                    >
                      <Plus className="w-3 h-3" /> From Library
                    </button>
                    <button
                      onClick={() => addOperation(null)}
                      className="px-3 py-1.5 text-xs bg-industrial-surface-highlight border border-industrial-border rounded-lg text-industrial-text-secondary hover:border-industrial-safety transition-colors flex items-center gap-1"
                      data-testid="add-manual-op-btn"
                    >
                      <Plus className="w-3 h-3" /> Manual
                    </button>
                  </div>
                </div>

                {formOps.length === 0 ? (
                  <div className="text-center py-6 border border-dashed border-industrial-border rounded-lg">
                    <FileText className="w-8 h-8 mx-auto text-industrial-text-secondary mb-2" />
                    <p className="text-xs text-industrial-text-secondary">Add operations from your saved programs or manually</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formOps.map((op, i) => (
                      <div key={i} className="bg-industrial-bg border border-industrial-border rounded-lg p-3" data-testid={`op-row-${i}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex flex-col gap-0.5">
                            <button
                              onClick={() => moveOp(i, -1)}
                              disabled={i === 0}
                              className="p-0.5 rounded hover:bg-industrial-surface-highlight disabled:opacity-20"
                              data-testid={`op-move-up-${i}`}
                            >
                              <ChevronUp className="w-3 h-3 text-industrial-text-secondary" />
                            </button>
                            <button
                              onClick={() => moveOp(i, 1)}
                              disabled={i === formOps.length - 1}
                              className="p-0.5 rounded hover:bg-industrial-surface-highlight disabled:opacity-20"
                              data-testid={`op-move-down-${i}`}
                            >
                              <ChevronDown className="w-3 h-3 text-industrial-text-secondary" />
                            </button>
                          </div>
                          <GripVertical className="w-4 h-4 text-industrial-text-secondary/40" />
                          <div className="flex-1 min-w-0">
                            <input
                              type="text"
                              value={op.program_name}
                              onChange={(e) => updateOp(i, 'program_name', e.target.value)}
                              className="w-full bg-transparent text-sm font-medium text-industrial-text border-none outline-none"
                              placeholder="Operation name"
                              data-testid={`op-name-${i}`}
                            />
                          </div>
                          <button
                            onClick={() => removeOperation(i)}
                            className="p-1.5 rounded hover:bg-red-500/20 transition-colors"
                            data-testid={`op-remove-${i}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        </div>
                        <div className="grid grid-cols-4 gap-2 pl-8">
                          <div>
                            <label className="block text-[10px] text-industrial-text-secondary mb-0.5">Op #</label>
                            <input
                              type="number"
                              value={op.op_number}
                              onChange={(e) => updateOp(i, 'op_number', parseInt(e.target.value) || 0)}
                              className="w-full bg-industrial-surface border border-industrial-border rounded px-2 py-1 text-xs text-industrial-text"
                              data-testid={`op-number-${i}`}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-industrial-text-secondary mb-0.5">Setup (min)</label>
                            <input
                              type="number"
                              value={op.est_setup_min}
                              onChange={(e) => updateOp(i, 'est_setup_min', parseFloat(e.target.value) || 0)}
                              className="w-full bg-industrial-surface border border-industrial-border rounded px-2 py-1 text-xs text-industrial-text"
                              data-testid={`op-setup-${i}`}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-industrial-text-secondary mb-0.5">Cycle (min)</label>
                            <input
                              type="number"
                              value={op.est_cycle_min}
                              onChange={(e) => updateOp(i, 'est_cycle_min', parseFloat(e.target.value) || 0)}
                              className="w-full bg-industrial-surface border border-industrial-border rounded px-2 py-1 text-xs text-industrial-text"
                              data-testid={`op-cycle-${i}`}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-industrial-text-secondary mb-0.5">Description</label>
                            <input
                              type="text"
                              value={op.description}
                              onChange={(e) => updateOp(i, 'description', e.target.value)}
                              className="w-full bg-industrial-surface border border-industrial-border rounded px-2 py-1 text-xs text-industrial-text"
                              placeholder="Optional"
                              data-testid={`op-desc-${i}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Totals */}
                {formOps.length > 0 && (
                  <div className="flex items-center gap-4 text-xs mt-3 p-2 bg-industrial-safety/10 border border-industrial-safety/20 rounded-lg">
                    <span className="text-industrial-text-secondary">Setup: <span className="font-medium text-industrial-text">{formatTime(totalSetup)}</span></span>
                    <span className="text-industrial-text-secondary">Cycle: <span className="font-medium text-industrial-text">{formatTime(totalCycle)}</span></span>
                    <span className="ml-auto font-medium text-industrial-safety">Total: {formatTime(totalSetup + totalCycle)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Editor Footer */}
            <div className="flex items-center gap-3 p-4 border-t border-industrial-border">
              <button
                onClick={() => setShowEditor(false)}
                className="flex-1 py-3 bg-industrial-surface-highlight border border-industrial-border rounded-lg font-medium text-industrial-text"
                data-testid="cancel-plan-btn"
              >
                Cancel
              </button>
              <button
                onClick={savePlan}
                disabled={!formName.trim()}
                className="flex-1 py-3 bg-industrial-safety text-black rounded-lg font-medium disabled:opacity-40 hover:bg-industrial-safety/90 transition-colors flex items-center justify-center gap-2"
                data-testid="save-plan-btn"
              >
                <CheckCircle className="w-4 h-4" />
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Program Picker Modal ──────────────────────────── */}
      {showProgramPicker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
          <div className="bg-industrial-surface border border-industrial-border rounded-xl w-full max-w-md max-h-[70vh] flex flex-col" data-testid="program-picker-modal">
            <div className="flex items-center justify-between p-4 border-b border-industrial-border">
              <h3 className="font-heading font-bold text-industrial-text text-sm">Select Program</h3>
              <button
                onClick={() => setShowProgramPicker(false)}
                className="p-1.5 hover:bg-industrial-surface-highlight rounded-lg"
              >
                <X className="w-4 h-4 text-industrial-text-secondary" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {programs.length === 0 ? (
                <p className="text-xs text-industrial-text-secondary text-center py-4">No saved programs found</p>
              ) : (
                programs.map(prog => (
                  <button
                    key={prog.id}
                    onClick={() => addOperation(prog)}
                    className="w-full text-left bg-industrial-bg border border-industrial-border rounded-lg p-3 hover:border-industrial-safety/50 transition-colors"
                    data-testid={`pick-program-${prog.id}`}
                  >
                    <div className="text-sm font-medium text-industrial-text truncate">{prog.name}</div>
                    <div className="text-xs text-industrial-text-secondary truncate mt-0.5">
                      {prog.type?.toUpperCase()} {prog.controller?.toUpperCase()}
                      {prog.description && ` — ${prog.description}`}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── G-Code Viewer Modal ───────────────────────────── */}
      {viewingGcode && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
          <div className="bg-industrial-surface border border-industrial-border rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-industrial-border">
              <div>
                <h3 className="font-heading font-bold text-industrial-text">{viewingGcode.name}</h3>
                <p className="text-xs text-industrial-text-secondary mt-0.5">{viewingGcode.type?.toUpperCase()} {viewingGcode.controller?.toUpperCase()}</p>
              </div>
              <button
                onClick={() => setViewingGcode(null)}
                className="p-2 hover:bg-industrial-surface-highlight rounded-lg"
                data-testid="close-gcode-viewer-btn"
              >
                <X className="w-5 h-5 text-industrial-text-secondary" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <pre className="font-mono text-sm text-industrial-text bg-industrial-bg p-4 rounded-lg overflow-x-auto whitespace-pre-wrap">
                {viewingGcode.gcode || 'No G-code available'}
              </pre>
            </div>
            <div className="flex items-center gap-3 p-4 border-t border-industrial-border">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(viewingGcode.gcode || '');
                }}
                className="flex-1 py-2.5 bg-industrial-surface-highlight border border-industrial-border rounded-lg font-medium text-industrial-text flex items-center justify-center gap-2 text-sm"
              >
                <Copy className="w-4 h-4" /> Copy
              </button>
              <button
                onClick={() => {
                  const blob = new Blob([viewingGcode.gcode || ''], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${viewingGcode.name}.nc`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                className="flex-1 py-2.5 bg-industrial-safety text-black rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
              >
                <Download className="w-4 h-4" /> Download .nc
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Print Traveler (hidden, shown only for print) ─── */}
      {printingPlan && (
        <div
          className="fixed inset-0 z-[70] bg-white print-traveler-overlay"
          data-testid="print-traveler"
          onClick={() => setPrintingPlan(null)}
        >
          <div className="max-w-[800px] mx-auto p-8 text-black" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="border-b-2 border-black pb-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold">JOB TRAVELER</h1>
                  <p className="text-sm text-gray-600">G-Code Pro — CNC Routing Sheet</p>
                </div>
                <div className="text-right text-sm">
                  <div>Date: {new Date().toLocaleDateString()}</div>
                  <div>Plan ID: {printingPlan.id?.slice(0, 8)}</div>
                </div>
              </div>
            </div>

            {/* Part Info */}
            <div className="grid grid-cols-3 gap-4 mb-6 text-sm">
              <div className="border border-gray-400 rounded p-2">
                <div className="text-[10px] text-gray-500 uppercase font-bold">Job Name</div>
                <div className="font-bold text-lg">{printingPlan.name}</div>
              </div>
              <div className="border border-gray-400 rounded p-2">
                <div className="text-[10px] text-gray-500 uppercase font-bold">Part Number</div>
                <div className="font-bold text-lg">{printingPlan.part_number || '—'}</div>
              </div>
              <div className="border border-gray-400 rounded p-2">
                <div className="text-[10px] text-gray-500 uppercase font-bold">Material</div>
                <div className="font-bold">{MATERIAL_LABELS[printingPlan.material] || printingPlan.material || '—'}</div>
              </div>
            </div>

            {printingPlan.notes && (
              <div className="mb-4 p-2 border border-gray-300 rounded text-sm bg-gray-50">
                <span className="font-bold">Notes: </span>{printingPlan.notes}
              </div>
            )}

            {/* Operations Table */}
            <table className="w-full border-collapse text-sm mb-6">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="border border-gray-400 px-2 py-2 text-left w-12">Op #</th>
                  <th className="border border-gray-400 px-2 py-2 text-left">Operation</th>
                  <th className="border border-gray-400 px-2 py-2 text-left">Description</th>
                  <th className="border border-gray-400 px-2 py-2 text-left">Tooling</th>
                  <th className="border border-gray-400 px-2 py-2 text-center w-16">Setup</th>
                  <th className="border border-gray-400 px-2 py-2 text-center w-16">Cycle</th>
                  <th className="border border-gray-400 px-2 py-2 text-center w-20">Sign-Off</th>
                </tr>
              </thead>
              <tbody>
                {(printingPlan.operations || []).map((op, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-400 px-2 py-2 font-mono font-bold text-center">{op.op_number}</td>
                    <td className="border border-gray-400 px-2 py-2 font-medium">{op.program_name}</td>
                    <td className="border border-gray-400 px-2 py-2 text-gray-600">{op.description || '—'}</td>
                    <td className="border border-gray-400 px-2 py-2 text-xs text-gray-600">{op.tools_summary || '—'}</td>
                    <td className="border border-gray-400 px-2 py-2 text-center font-mono">{formatTime(op.est_setup_min)}</td>
                    <td className="border border-gray-400 px-2 py-2 text-center font-mono">{formatTime(op.est_cycle_min)}</td>
                    <td className="border border-gray-400 px-2 py-3 text-center">
                      <div className="w-full h-6 border-b border-gray-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-200 font-bold">
                  <td className="border border-gray-400 px-2 py-2" colSpan={4} style={{ textAlign: 'right' }}>TOTALS:</td>
                  <td className="border border-gray-400 px-2 py-2 text-center font-mono">{formatTime(printingPlan.total_setup_min)}</td>
                  <td className="border border-gray-400 px-2 py-2 text-center font-mono">{formatTime(printingPlan.total_cycle_min)}</td>
                  <td className="border border-gray-400 px-2 py-2 text-center font-mono">
                    {formatTime((printingPlan.total_setup_min || 0) + (printingPlan.total_cycle_min || 0))}
                  </td>
                </tr>
              </tfoot>
            </table>

            {/* QC / Sign-off Footer */}
            <div className="grid grid-cols-3 gap-6 mt-8 text-sm">
              <div>
                <div className="border-b border-black pb-8 mb-1" />
                <div className="text-xs text-gray-500">Operator Signature / Date</div>
              </div>
              <div>
                <div className="border-b border-black pb-8 mb-1" />
                <div className="text-xs text-gray-500">QC Inspector / Date</div>
              </div>
              <div>
                <div className="border-b border-black pb-8 mb-1" />
                <div className="text-xs text-gray-500">Supervisor / Date</div>
              </div>
            </div>

            {/* Close button (screen only) */}
            <div className="mt-6 text-center no-print">
              <button
                onClick={() => setPrintingPlan(null)}
                className="px-6 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-700"
                data-testid="close-traveler-btn"
              >
                Close Traveler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobPlansPage;
